import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Measure the element the canvas renders into (TV1-2291 · S2).
 *
 * The grid used to lay itself out over a hard-coded 827px regardless of the
 * space it was given, so a report was a narrow centred column on a wide monitor
 * and overflowed a phone. This is what replaces that constant.
 *
 * Observe a container whose width does NOT depend on the canvas, or the
 * ResizeObserver feeds itself: canvas grows → container grows → callback →
 * canvas grows. Both call sites observe an outer, `width: 100%` wrapper for
 * exactly this reason; the canvas sits inside it with an explicit pixel width
 * (which, at zoom ≠ 1, is deliberately not the container's width).
 */
export const useContainerWidth = <T extends HTMLElement>() => {
  const ref = useRef<T>( null );
  const [ width, setWidth ] = useState( 0 );

  useLayoutEffect(() => {
    const element = ref.current;
    if ( !element ) return;

    // Seed synchronously. Waiting for the observer's first async callback would
    // paint one frame at width 0 — every widget collapsed — before snapping to
    // size, which reads as a flicker on every mount.
    const measure = ( value: number ) => setWidth(( prev ) => (
      Math.abs( prev - value ) < 0.5 ? prev : value
    ));
    measure( element.clientWidth );

    // jsdom has no ResizeObserver unless a test stubs one in. Falling back to
    // the seeded measurement keeps the component renderable there.
    if ( typeof ResizeObserver === 'undefined' ) return;

    const observer = new ResizeObserver(( entries ) => {
      // `contentRect` excludes the wrapper's own padding, which is the space
      // actually available to the canvas — `clientWidth` would include it.
      const next = entries[0]?.contentRect.width;
      measure( typeof next === 'number' ? next : element.clientWidth );
    });
    observer.observe( element );
    return () => observer.disconnect();
  }, []);

  return [ ref, width ] as const;
};
